import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Handshake, Calendar, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { programs, impactStats } from '../data/mock';

const Home = () => {
  const iconMap = {
    Users: Users,
    Target: Target,
    Handshake: Handshake,
    Calendar: Calendar
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1529209076408-5a115ec9f1c6)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-800/75"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Empowering Communities,
            <br />
            Building Sustainable Futures
          </h1>
          <p className="text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
            Together, we partner with communities to create sustainable pathways to education, 
            opportunity, and local leadership.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://buy.stripe.com/6oU4gsg9r9Nz3ce2GU0kE00" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'donate_click', {
                    event_category: 'fundraising',
                    event_label: 'stripe_payment_link'
                  });
                }
              }}
            >
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-8 h-12">
                Support Our Work
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/get-involved">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 h-12">
                Get Involved
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-6">
              <Heart className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              To empower communities by supporting education, youth development, sustainable 
              livelihoods, and local leadership initiatives that create lasting social and 
              economic impact.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => {
              const IconComponent = iconMap[stat.icon];
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-600 rounded-lg mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-emerald-700 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Do</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We focus on four core program areas to create comprehensive, sustainable impact in the communities we serve.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program) => (
              <Card key={program.id} className="group hover:shadow-xl transition-all duration-300 border-gray-200 overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
                    {program.title}
                  </h3>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {program.description}
                  </p>
                  <Link
                    to="/programs"
                    className="inline-flex items-center text-emerald-700 font-semibold hover:text-emerald-800 transition-colors"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8">
                View All Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Delinam Foundation */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Delinam Foundation?
            </h2>
            <Card className="border-2 border-emerald-200 shadow-lg">
              <CardContent className="p-10">
                <div className="space-y-6 text-gray-700 leading-relaxed text-center">
                  <p className="text-lg">
                    We believe lasting impact happens when communities are supported to lead their own 
                    development. Your support helps fund programs that equip young people, strengthen 
                    livelihoods, and build local leadership capacity.
                  </p>
                  <p className="text-lg font-medium text-emerald-800">
                    Every contribution directly supports community-driven solutions that create measurable, 
                    long-term change.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
            Join us in creating sustainable change. Every contribution, big or small, 
            helps build stronger communities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://buy.stripe.com/6oU4gsg9r9Nz3ce2GU0kE00" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'donate_click', {
                    event_category: 'fundraising',
                    event_label: 'stripe_payment_link'
                  });
                }
              }}
            >
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-8 h-12">
                Support Our Work
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 h-12">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
